import React from 'react';
import {FormattedMessage} from 'react-intl';

import {type Channel} from 'mattermost-redux/types/channels';
import {BUTTON_SIZE} from 'packages/ui-kit';

import LocalizedIcon from 'components/localized_icon';
import FollowButton from 'components/threading/common/follow_button';
import KeyboardShortcutSequence, {KEYBOARD_SHORTCUTS} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';

import {browserHistory} from 'utils/browser_history';
import Constants, {RHSStates} from 'utils/constants';
import {t} from 'utils/i18n';
import HideWhenChannelIsReadOnly from '../../features/channels/components/read-only';
import {ArrowExpandIcon, ArrowMinimizeIcon} from '@time-webkit/all/icons';
import {localizeMessage} from 'utils/utils';

import RhsHeaderChannelButton from './rhs_header_channel_button';
import RhsHeaderIconButton from './rhs_header_icon_button';

import {type PluginItem} from '.';

interface RhsHeaderPostProps {
    isExpanded: boolean;
    isMobileView: boolean;
    rootPostId: string;
    previousRhsState?: string;
    relativeTeamUrl: string;
    channel: Channel;
    isFollowingThread?: boolean | null;
    currentTeamId: string;
    currentUserId: string;
    pluginItems: PluginItem[];
    setRhsExpanded: (b: boolean) => void;
    showSearchResults: () => void;
    showFlaggedPosts: () => void;
    showPinnedPosts: () => void;
    goBack: () => void;
    closeRightHandSide: (e?: React.MouseEvent) => void;
    toggleRhsExpanded: () => void;
    changeThreadFollowOnServer: (payload: {
        userId: string;
        teamId: string;
        threadId: string;
        following: boolean;
    }) => void;
}

const WIDTH_TO_OVERFLOW = 380;

type State = {
    shouldOverflow: boolean;
}

export default class RhsHeaderPost extends React.PureComponent<RhsHeaderPostProps> {
    state: State = {
        shouldOverflow: false,
    }

    private rhsHeaderPostRef = React.createRef<HTMLDivElement>();
    private resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
            this.setState({shouldOverflow: entry.contentRect.width <= WIDTH_TO_OVERFLOW});
        }
    })

    handleBack = () => {
        switch (this.props.previousRhsState) {
        case RHSStates.SEARCH:
        case RHSStates.FLAG:
        case RHSStates.PIN:
        case RHSStates.USER_INFO:
            this.props.goBack();
            break;
        default:
            break;
        }
    };

    handleJumpClick = () => {
        if (this.props.isMobileView) {
            this.props.closeRightHandSide();
        }

        this.props.setRhsExpanded(false);
        const teamUrl = this.props.relativeTeamUrl;
        browserHistory.push(`${teamUrl}/pl/${this.props.rootPostId}`);
    };

    handleFollowChange = () => {
        const {currentTeamId, currentUserId, rootPostId, isFollowingThread} = this.props;

        this.props.changeThreadFollowOnServer({
            userId: currentUserId,
            teamId: currentTeamId,
            threadId: rootPostId,
            following: !isFollowingThread,
        });
    };

    componentDidMount(): void {
        if (!this.rhsHeaderPostRef?.current) {
            return;
        }

        this.resizeObserver.observe(this.rhsHeaderPostRef.current);
    }

    componentWillUnmount(): void {
        this.resizeObserver.disconnect();
    }

    render() {
        let back;
        const {isFollowingThread} = this.props;
        const closeSidebarTooltipText = (
            <FormattedMessage
                id='rhs_header.closeSidebarTooltip'
                defaultMessage='Close'
            />
        );

        let backToResultsTooltipText;

        switch (this.props.previousRhsState) {
        case RHSStates.SEARCH:
            backToResultsTooltipText = (
                <FormattedMessage
                    id='rhs_header.backToResultsTooltip'
                    defaultMessage='Back to search results'
                />
            );
            break;
        case RHSStates.FLAG:
            backToResultsTooltipText = (
                <FormattedMessage
                    id='rhs_header.backToFlaggedTooltip'
                    defaultMessage='Back to saved posts'
                />
            );
            break;
        case RHSStates.PIN:
            backToResultsTooltipText = (
                <FormattedMessage
                    id='rhs_header.backToPinnedTooltip'
                    defaultMessage='Back to pinned posts'
                />
            );
            break;
        }

        const expandSidebarTooltipText = (
            <FormattedMessage
                id='rhs_header.expandSidebarTooltip'
                defaultMessage='Expand the right sidebar'
            />
        );

        const expandSidebarTooltipNode = (
            <KeyboardShortcutSequence
                shortcut={KEYBOARD_SHORTCUTS.navExpandSidebar}
                hideDescription={true}
                isInsideTooltip={true}
            />
        );

        const shrinkSidebarTooltipText = (
            <FormattedMessage
                id='rhs_header.collapseSidebarTooltip'
                defaultMessage='Collapse the right sidebar'
            />
        );

        const shrinkSidebarTooltipNode = (
            <KeyboardShortcutSequence
                shortcut={KEYBOARD_SHORTCUTS.navExpandSidebar}
                hideDescription={true}
                isInsideTooltip={true}
            />
        );

        const channelName = ('display_name' in this.props.channel) && this.props.channel.display_name;

        if (backToResultsTooltipText) {
            back = (
                <RhsHeaderIconButton
                    key='back-button'
                    className='sidebar--right__back'
                    size={BUTTON_SIZE.XSMALL}
                    icon={(
                        <LocalizedIcon
                            className='icon icon-arrow-back-ios'
                            ariaLabel={{id: t('generic_icons.back'), defaultMessage: 'Back Icon'}}
                        />
                    )}
                    onPress={this.handleBack}
                    tooltipDelayShow={Constants.OVERLAY_TIME_DELAY}
                    tooltipText={backToResultsTooltipText}
                />
            );
        }

        return (
            <div className='sidebar--right__header' ref={this.rhsHeaderPostRef}>
                <span className='sidebar--right__title'>
                    {back}
                    <FormattedMessage
                        id='rhs_header.details'
                        defaultMessage='Thread'
                    />
                    {channelName && (
                        <RhsHeaderChannelButton
                            onClick={this.handleJumpClick}
                            channelName={channelName}
                        />
                    )}
                </span>
                <div className='controls'>
                    <HideWhenChannelIsReadOnly channelId={this.props.channel.id}>
                        <FollowButton
                            className='sidebar--right__follow__thread'
                            isFollowing={Boolean(isFollowingThread)}
                            onClick={this.handleFollowChange}
                            shouldOverflow={this.state.shouldOverflow}
                        />
                    </HideWhenChannelIsReadOnly>
                    {this.props.pluginItems.map((item) => (
                        <RhsHeaderIconButton
                            key={item.key}
                            icon={item.icon}
                            onPress={item.onPress}
                            tooltipDelayShow={Constants.OVERLAY_TIME_DELAY}
                            tooltipText={item.tooltipText}
                            tooltipNode={item.tooltipNode}
                        />
                    ))}
                    <RhsHeaderIconButton
                        key='resize-button'
                        icon={this.props.isExpanded ? (
                            <LocalizedIcon
                                className='icon icon-arrow-collapse'
                                ariaLabel={{
                                    id: t('rhs_header.collapseSidebarTooltip.icon'),
                                    defaultMessage: 'Collapse Sidebar Icon',
                                }}
                            />
                        ) : (
                            <LocalizedIcon
                                className='icon icon-arrow-expand'
                                ariaLabel={{
                                    id: t('rhs_header.expandSidebarTooltip.icon'),
                                    defaultMessage: 'Expand Sidebar Icon',
                                }}
                            />
                        )}
                        onPress={this.props.toggleRhsExpanded}
                        tooltipDelayShow={Constants.OVERLAY_TIME_DELAY}
                        tooltipText={this.props.isExpanded ? shrinkSidebarTooltipText : expandSidebarTooltipText}
                        tooltipNode={this.props.isExpanded ? shrinkSidebarTooltipNode : expandSidebarTooltipNode}
                    />
                    <RhsHeaderIconButton
                        key='close-button'
                        icon={
                            <LocalizedIcon
                                className='icon icon-close'
                                ariaLabel={{
                                    id: t('rhs_header.closeTooltip.icon'),
                                    defaultMessage: 'Close Sidebar Icon',
                                }}
                            />
                        }
                        onPress={this.props.closeRightHandSide}
                        tooltipDelayShow={Constants.OVERLAY_TIME_DELAY}
                        tooltipText={closeSidebarTooltipText}
                    />
                </div>
            </div>
        );
    }
}
